import React, { useState, useEffect, useCallback } from "react";
import { apiGet } from "../../../services/api";
import { safeDate } from "../../../utils/dateUtils";
import { applyChartMutationClient, type ChartMutationFrame } from "./applyChartMutationClient";
import ChartBig from "./ChartBig";
import "./ChartHistoryPlayer.css";
import { eventBus } from "../events/eventBus";

const HISTORY_FIT_X = 1035;
const HISTORY_FIT_Y = 582;
const HISTORY_FIT_S = 0.16;
/** When replay speed exceeds this, skip fade animations to avoid CPU spikes. */
const REPLAY_SPEED_THRESHOLD = 1.5;

interface ChartFrame {
  symbol?: string;
  tooth?: string;
  surface?: string;
  multi_tooth?: boolean;
  timestamp?: number | string;
}

interface ChartState {
  states?: Record<string, string>;
  surfaces?: Record<string, unknown>;
  flags?: Record<string, unknown>;
  movements?: Record<string, unknown>;
}

const emptyState: ChartState = {
  states: {},
  surfaces: {},
  flags: {},
  movements: {},
};

export interface ChartHistoryPlayerProps {
  patientId?: string;
  activeTooth?: string | null;
  onToothClick?: (toothId: string) => void;
  isLocked?: boolean;
  onVisitSelect?: (visitId: string) => void;
  currentChartData?: ChartState | null;
  selectedVisitId?: string | null;
  /** Bump to force re-fetch chart history (e.g. after mutations from Clinical tab). */
  refreshKey?: string | number;
  /** When > REPLAY_SPEED_THRESHOLD, fade animations are disabled. */
  replaySpeed?: number;
}

export default function ChartHistoryPlayer({
  patientId,
  activeTooth,
  onToothClick,
  isLocked,
  onVisitSelect,
  currentChartData,
  selectedVisitId,
  refreshKey,
  replaySpeed = 1,
}: ChartHistoryPlayerProps) {
  const [frames, setFrames] = useState<ChartFrame[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [replayState, setReplayState] = useState<ChartState>(emptyState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rebuildUpTo = useCallback((allFrames: ChartFrame[], upToIndex: number): ChartState => {
    const state: ChartState = {
      states: {},
      surfaces: {},
      flags: {},
      movements: {},
    };

    for (let i = 0; i <= upToIndex && i < allFrames.length; i++) {
      const f = allFrames[i];
      applyChartMutationClient(state, f as unknown as ChartMutationFrame);
    }

    return state;
  }, []);

  // Load chart history for the selected visit (only when selectedVisitId changes; currentChartData is only used when clearing)
  useEffect(() => {
    if (!selectedVisitId) {
      setFrames([]);
      setCurrentIndex(null);
      setReplayState(
        currentChartData && typeof currentChartData === "object"
          ? currentChartData
          : emptyState
      );
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiGet(`/api/doctor/chart-history/${selectedVisitId}`);
        if (cancelled) return;
        const list = Array.isArray(res) ? (res as ChartFrame[]) : [];
        setFrames(list);

        if (list.length > 0) {
          const lastIdx = list.length - 1;
          setCurrentIndex(lastIdx);
          setReplayState(rebuildUpTo(list, lastIdx));
        } else {
          setCurrentIndex(null);
          setReplayState(emptyState);
        }
      } catch (e) {
        if (cancelled) return;
        console.error("Failed to load chart history", e);
        setError("Unable to load dental chart history for this visit.");
        setFrames([]);
        setCurrentIndex(null);
        setReplayState(emptyState);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [selectedVisitId, refreshKey, rebuildUpTo]);

  // Rebuild chart state when slider changes
  useEffect(() => {
    if (!frames.length) return;
    if (currentIndex == null || currentIndex < 0) return;

    const clamped = Math.min(Math.max(currentIndex, 0), frames.length - 1);
    if (clamped !== currentIndex) {
      setCurrentIndex(clamped);
      return;
    }

    const rebuilt = rebuildUpTo(frames, clamped);
    setReplayState(rebuilt);
  }, [frames, currentIndex, rebuildUpTo]);

  const handleSlider = (idx: number) => {
    if (!frames.length) return;
    const clamped = Math.min(Math.max(idx, 0), frames.length - 1);
    setCurrentIndex(clamped);
  };

  const activeFrame =
    currentIndex != null && frames[currentIndex] ? frames[currentIndex] : null;

  // Emit a TOOTH_EVENT for the active frame so animations can react.
  useEffect(() => {
    if (!activeFrame || !activeFrame.tooth) return;
    eventBus.emit("TOOTH_EVENT", {
      encounterId: selectedVisitId, // until real encounter id is wired
      tooth: activeFrame.tooth,
      surface: activeFrame.surface,
      type: "procedure",
      payload: {
        symbol: activeFrame.symbol,
        multi_tooth: activeFrame.multi_tooth,
      },
      timestamp: activeFrame.timestamp || Date.now(),
    });
  }, [activeFrame, selectedVisitId]);

  if (loading) {
    return (
      <div className="chart-history-player chart-history-player-empty">
        <div className="empty-state">
          <span className="empty-icon">⏳</span>
          <p>Loading dental chart history…</p>
          <small>Please wait</small>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-history-player chart-history-player-empty">
        <div className="empty-state">
          <span className="empty-icon">⚠️</span>
          <p>Chart history unavailable.</p>
          <small>{error}</small>
        </div>
      </div>
    );
  }

  /* When no visit selected, show prompt only. */
  if (!selectedVisitId) {
    return (
      <div className="chart-history-player chart-history-player-empty">
        <div className="empty-state">
          <span className="empty-icon">🦷</span>
          <p>Select a visit</p>
          <small>Choose a visit from the list to view chart history.</small>
        </div>
      </div>
    );
  }

  /* Always render the tooth chart when a visit is selected so zoom/pan/drag work.
   * When there are no frames, replayState is empty; show a caption. */
  const hasFrames = frames.length > 0;

  return (
    <div className="chart-history-player">
      <div className="chart-stage">
        {!hasFrames && (
          <div className="chart-history-empty-caption" aria-live="polite">
            <span className="empty-icon">🦷</span>
            <p>No chart history yet.</p>
            <small>Procedures for this visit will appear here.</small>
          </div>
        )}
        <ChartBig
          toothStates={replayState.states ?? {}}
          surfacesMap={replayState.surfaces ?? {}}
          flagsMap={replayState.flags ?? {}}
          movementsMap={replayState.movements ?? {}}
          selectedTeeth={activeTooth ? [activeTooth] : []}
          onToothClick={onToothClick}
          isLocked={isLocked}
          animateReplay={replaySpeed <= REPLAY_SPEED_THRESHOLD}
          fitX={HISTORY_FIT_X}
          fitY={HISTORY_FIT_Y}
          fitS={HISTORY_FIT_S}
          title={
            activeFrame && activeFrame.timestamp
              ? `Dental Chart — ${safeDate(activeFrame.timestamp)?.toLocaleString(
                [],
                {
                  day: "numeric",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                }
              ) ?? "—"}`
              : "Dental Chart"
          }
        />
      </div>

      {hasFrames && (
        <div className="history-controls">
          <div className="slider-row">
            <button
              type="button"
              className="hist-btn"
              onClick={() =>
                handleSlider(
                  Math.max(0, (currentIndex ?? frames.length - 1) - 1)
                )
              }
              disabled={currentIndex === 0 || frames.length === 0}
            >
              ◀︎
            </button>

            <input
              type="range"
              min={0}
              max={Math.max(0, frames.length - 1)}
              value={currentIndex ?? frames.length - 1}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSlider(Number(e.target.value))}
              className="hist-slider"
              disabled={frames.length === 0}
            />

            <button
              type="button"
              className="hist-btn"
              onClick={() =>
                handleSlider(
                  Math.min(
                    frames.length - 1,
                    (currentIndex ?? frames.length - 1) + 1
                  )
                )
              }
              disabled={
                currentIndex === frames.length - 1 || frames.length === 0
              }
            >
              ▶︎
            </button>

            <span className="hist-date">
              {activeFrame && activeFrame.timestamp
                ? (safeDate(activeFrame.timestamp)?.toLocaleDateString([], {
                  day: "numeric",
                  month: "short",
                }) ?? "—")
                : "—"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
